import type { PlayerState, Equipment } from "@/types/game";
import type { DeckCard } from "@/types/cards";
import { statusDescription } from "@/engine/Status";
import { effectiveAttack, effectiveDefense, effectiveMagicDefense } from "@/engine/damage";
import { usePanelFx } from "@/hooks/usePanelFx";

export interface SeatPanelProps {
  player: PlayerState;
  isCurrent: boolean;
  isTargetable?: boolean;
  onTarget?: () => void;
  isHuman?: boolean;
}

interface EquipSlot {
  key: keyof Equipment;
  label: string;
}

const SLOTS: EquipSlot[] = [
  { key: "weapon", label: "武" },
  { key: "armor", label: "防" },
  { key: "accessory", label: "饰" },
];

function statusTitle(name: string, remaining: number): string {
  const desc = statusDescription(name);
  const dur = remaining === -1 ? "永久" : `${remaining}回合`;
  return desc ? `${name}：${desc}（剩余${dur}）` : `${name}（剩余${dur}）`;
}

function heroSkillsTitle(player: PlayerState): string {
  if (!player.hero.skills || player.hero.skills.length === 0) return "";
  return player.hero.skills
    .map((s) => {
      const cost = s.manaCost ? `（耗魔${s.manaCost}）` : "";
      const type = s.type === "active" ? "[主动]" : "[被动]";
      return `${type} ${s.name}${cost}: ${s.description}`;
    })
    .join("\n");
}

function equipTooltip(card: DeckCard | undefined, slot: string): string {
  if (!card) return slot;
  const parts: string[] = [`${card.name}（${slot}）`];
  const bonuses: string[] = [];
  if (card.attackBonus) bonuses.push(`+${card.attackBonus} 攻击`);
  if (card.defenseBonus) bonuses.push(`+${card.defenseBonus} 防御`);
  if (card.magicDefenseBonus) bonuses.push(`+${card.magicDefenseBonus} 魔御`);
  if (card.rangeBonus) bonuses.push(`+${card.rangeBonus} 射程`);
  if (bonuses.length > 0) parts.push(bonuses.join(", "));
  if (card.effectText) parts.push(card.effectText);
  return parts.join("\n");
}

export default function SeatPanel({
  player,
  isCurrent,
  isTargetable,
  onTarget,
  isHuman,
}: SeatPanelProps) {
  const hpPct =
    player.maxHp > 0
      ? Math.max(0, Math.min(100, (player.hp / player.maxHp) * 100))
      : 0;
  const mpPct =
    player.maxMp > 0
      ? Math.max(0, Math.min(100, (player.mp / player.maxMp) * 100))
      : 0;
  const teamColor = player.team === "A" ? "var(--gold)" : "var(--red)";
  const fx = usePanelFx(player.id);

  const cls = [
    "seat",
    `seat--team-${player.team.toLowerCase()}`,
    isCurrent ? "seat--current" : "",
    isTargetable ? "seat--targetable" : "",
    player.isDying ? "seat--dying" : "",
    !player.isAlive ? "seat--dead" : "",
    fx.animClass,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      key={fx.animKey || undefined}
      className={cls}
      style={{ borderLeftColor: teamColor }}
      onClick={isTargetable ? onTarget : undefined}
      role={isTargetable ? "button" : undefined}
    >
      {fx.floatDmg != null && (
        <span className={`float-dmg ${fx.floatDmg < 0 ? "float-dmg--dmg" : "float-dmg--heal"}`}>
          {fx.floatDmg > 0 ? "+" : ""}{fx.floatDmg}
        </span>
      )}
      <div className="seat__head">
        <span className="seat__team-badge" style={{ background: teamColor }}>
          {player.team}
        </span>
        <span className="seat__name" style={{ color: teamColor }} title={heroSkillsTitle(player)}>
          {player.hero.name}
        </span>
        {player.isDying && <span className="seat__dying-tag">濒死</span>}
        {!player.isAlive && <span className="seat__dead-tag">阵亡</span>}
        {!isHuman && (
          <span className="seat__hand-count" title="手牌数">
            手 {player.hand.length}
          </span>
        )}
      </div>
      <div className="seat__bar">
        <div className="seat__bar-label">HP</div>
        <div className="seat__bar-track">
          <div
            className="seat__bar-fill seat__bar-fill--hp"
            style={{ width: `${hpPct}%` }}
          />
        </div>
        <div className="seat__bar-num">
          {player.hp}/{player.maxHp}
        </div>
      </div>
      <div className="seat__bar">
        <div className="seat__bar-label">MP</div>
        <div className="seat__bar-track">
          <div
            className="seat__bar-fill seat__bar-fill--mp"
            style={{ width: `${mpPct}%` }}
          />
        </div>
        <div className="seat__bar-num">
          {player.mp}/{player.maxMp}
        </div>
      </div>
      <div className="seat__stats" title="当前攻防御（含装备+状态）">
        <span className="seat__stat">攻{effectiveAttack(player)}</span>
        <span className="seat__stat">防{effectiveDefense(player)}</span>
        <span className="seat__stat">御{effectiveMagicDefense(player)}</span>
      </div>
      <div className="seat__equip">
        {SLOTS.map((s) => {
          const c = player.equip[s.key];
          return (
            <div
              key={s.key}
              className={`seat__equip-cell${c ? "" : " seat__equip-cell--empty"}`}
              title={equipTooltip(c, s.label)}
            >
              {c ? c.name : s.label}
            </div>
          );
        })}
      </div>
      {player.statuses.length > 0 && (
        <div className="seat__statuses">
          {player.statuses.map((st) => (
            <span
              key={st.id}
              className="seat__status"
              title={statusTitle(st.name, st.remainingTurns)}
            >
              {st.name.slice(0, 1)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
